import React, { createContext, useState, useContext } from 'react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'

  // Signup with email/phone and password
  const signup = (userData) => {
    // In production, this would call a backend API
    const newUser = {
      id: Date.now(),
      email: userData.email || userData.phone,
      phone: userData.phone || '',
      firstName: userData.firstName,
      lastName: userData.lastName,
      password: userData.password, // In production, never store plain passwords
      createdAt: new Date().toISOString()
    };
    
    // Store in localStorage (for demo purposes)
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    users.push(newUser);
    localStorage.setItem('users', JSON.stringify(users));
    
    setUser(newUser);
    setAuthMode('login');
    setShowAuthModal(false);
    return newUser;
  };

  // Login with email/phone and password
  const login = (emailOrPhone, password) => {
    // In production, this would call a backend API
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const foundUser = users.find(u => 
      (u.email === emailOrPhone || u.phone === emailOrPhone) && 
      u.password === password
    );
    
    if (foundUser) {
      setUser(foundUser);
      setIsGuest(false);
      setShowAuthModal(false);
      return foundUser;
    } else {
      throw new Error('Invalid email/phone or password');
    }
  };

  // Guest checkout
  const loginAsGuest = (guestData) => {
    const guest = {
      id: `guest-${Date.now()}`,
      email: guestData.email,
      phone: guestData.phone,
      firstName: guestData.firstName,
      lastName: guestData.lastName,
      isGuest: true
    };
    
    setUser(guest);
    setIsGuest(true);
    setShowAuthModal(false);
    return guest;
  };

  // Logout
  const logout = () => {
    setUser(null);
    setIsGuest(false);
  };

  // Open auth modal
  const openAuthModal = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // Close auth modal
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isGuest,
      showAuthModal,
      authMode,
      signup,
      login,
      loginAsGuest,
      logout,
      openAuthModal,
      closeAuthModal,
      setAuthMode
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
